import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Loader2, ChevronLeft, Plus, X, Trash2, CheckCircle2, XCircle, Inbox, Send, Clock } from "lucide-react";
import { api, humanizeAxiosError } from "../lib/api";
import { toast } from "sonner";

const BLUE = "#2563EB";
const soles = (n) => `S/ ${Number(n || 0).toFixed(2)}`;
const fecha = (iso) => (iso ? new Date(iso).toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const ESTADO = {
  pendiente: { label: "Pendiente", color: "#CA8A04", Icon: Clock },
  aprobada: { label: "Aprobada", color: "#16A34A", Icon: CheckCircle2 },
  rechazada: { label: "Rechazada", color: "#DC2626", Icon: XCircle },
};
const LINEA = { descripcion: "", cantidad: 1, precio_unitario: 0 };

export default function CotizacionesPage() {
  const [tab, setTab] = useState("recibidas");
  const [data, setData] = useState(undefined);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ titulo: "", destinatario_email: "", descripcion: "", validez_dias: 15, items: [{ ...LINEA }] });
  const [guardando, setGuardando] = useState(false);
  const [accion, setAccion] = useState(null);

  const cargar = async (t = tab) => {
    setData(undefined);
    try { const { data } = await api.get("/cotizaciones", { params: { tipo: t } }); setData(data); }
    catch { setData(null); }
  };
  useEffect(() => { cargar(tab); /* eslint-disable-next-line */ }, [tab]);

  const totalForm = form.items.reduce((s, l) => s + (Number(l.cantidad) || 0) * (Number(l.precio_unitario) || 0), 0);

  const crear = async () => {
    if (!form.titulo.trim() || !form.destinatario_email.trim()) { toast.error("Título y correo del destinatario son obligatorios."); return; }
    setGuardando(true);
    try {
      await api.post("/cotizaciones", {
        titulo: form.titulo, destinatario_email: form.destinatario_email, descripcion: form.descripcion,
        validez_dias: Number(form.validez_dias) || 15,
        items: form.items.filter((l) => l.descripcion.trim()).map((l) => ({ descripcion: l.descripcion, cantidad: Number(l.cantidad) || 1, precio_unitario: Number(l.precio_unitario) || 0 })),
      });
      toast.success("Cotización enviada.");
      setModal(false);
      setForm({ titulo: "", destinatario_email: "", descripcion: "", validez_dias: 15, items: [{ ...LINEA }] });
      setTab("enviadas");
      await cargar("enviadas");
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setGuardando(false); }
  };

  const responder = async (id, decision) => {
    let comentario = "";
    if (decision === "rechazada") { comentario = window.prompt("Motivo del rechazo (opcional):") || ""; }
    setAccion(id);
    try { await api.post(`/cotizaciones/${id}/responder`, { decision, comentario }); toast.success(`Cotización ${decision}.`); await cargar(); }
    catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setAccion(null); }
  };
  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar esta cotización?")) return;
    setAccion(id);
    try { await api.delete(`/cotizaciones/${id}`); await cargar(); toast.success("Cotización eliminada."); }
    catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setAccion(null); }
  };

  const setLinea = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));
  const r = data?.resumen;

  return (
    <div className="px-5 md:px-8 py-8 max-w-4xl" data-testid="cotizaciones-page">
      <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800"><ChevronLeft size={16} /> Volver al Ecosistema</Link>
      <div className="mt-4 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight inline-flex items-center gap-3"><FileText size={30} style={{ color: BLUE }} /> Cotizaciones</h1>
          <p className="mt-2 text-slate-600">Cotizaciones creadas, recibidas, aprobadas o pendientes.</p>
        </div>
        <button onClick={() => setModal(true)} data-testid="cotizacion-nueva" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-bold hover:brightness-110" style={{ backgroundColor: BLUE }}><Plus size={18} /> Nueva cotización</button>
      </div>

      {r && (
        <div className="mt-6 grid grid-cols-3 gap-3">
          {[["Recibidas", r.recibidas], ["Enviadas", r.enviadas], ["Pendientes", r.pendientes]].map(([l, v]) => (
            <div key={l} className="bg-white rounded-2xl border border-slate-100 shadow-soft p-4 text-center"><p className="text-2xl font-extrabold text-slate-900">{v}</p><p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{l}</p></div>
          ))}
        </div>
      )}

      <div className="mt-6 flex gap-2">
        {[["recibidas", "Recibidas", Inbox], ["enviadas", "Enviadas", Send]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`cotizaciones-tab-${k}`} className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border transition-colors ${tab === k ? "bg-slate-900 text-white border-slate-900" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}><Icon size={15} /> {l}</button>
        ))}
      </div>

      <div className="mt-4 space-y-3">
        {data === undefined && <div className="flex items-center gap-2 text-slate-500 py-12"><Loader2 className="animate-spin" size={16} /> Cargando…</div>}
        {data === null && <p className="text-sm text-slate-400 py-8">No se pudieron cargar las cotizaciones.</p>}
        {data && data.items.length === 0 && (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-100" data-testid="cotizaciones-vacio">
            <FileText size={40} className="mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500">{tab === "recibidas" ? "No has recibido cotizaciones." : "No has enviado cotizaciones."}</p>
          </div>
        )}
        {data?.items.map((c) => {
          const e = ESTADO[c.estado] || ESTADO.pendiente; const EIcon = e.Icon;
          return (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid={`cotizacion-${c.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-bold text-slate-900">{c.titulo}</p>
                  <p className="text-xs text-slate-500">{c.es_emisor ? `Para: ${c.destinatario_nombre || c.destinatario_email}` : `De: ${c.emisor_nombre}`} · {fecha(c.created_at)}</p>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ color: e.color, backgroundColor: `${e.color}15` }}><EIcon size={12} /> {e.label}</span>
              </div>
              {c.descripcion && <p className="mt-2 text-sm text-slate-600">{c.descripcion}</p>}
              {c.items?.length > 0 && (
                <div className="mt-3 rounded-xl border border-slate-100 divide-y divide-slate-50">
                  {c.items.map((l, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-slate-700">{l.descripcion} <span className="text-slate-400">×{l.cantidad}</span></span>
                      <span className="font-semibold text-slate-900">{soles((l.cantidad || 0) * (l.precio_unitario || 0))}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                <span className="text-sm font-extrabold text-slate-900">Total: {soles(c.total)}</span>
                <div className="flex items-center gap-2">
                  {c.comentario_respuesta && <span className="text-xs text-slate-400 italic">"{c.comentario_respuesta}"</span>}
                  {!c.es_emisor && c.estado === "pendiente" && (
                    <>
                      <button onClick={() => responder(c.id, "aprobada")} disabled={accion === c.id} data-testid={`cotizacion-aprobar-${c.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-sm font-bold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"><CheckCircle2 size={14} /> Aprobar</button>
                      <button onClick={() => responder(c.id, "rechazada")} disabled={accion === c.id} data-testid={`cotizacion-rechazar-${c.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold border border-slate-300 text-slate-700 hover:bg-slate-100 disabled:opacity-50"><XCircle size={14} /> Rechazar</button>
                    </>
                  )}
                  {c.es_emisor && (
                    <button onClick={() => eliminar(c.id)} disabled={accion === c.id} data-testid={`cotizacion-eliminar-${c.id}`} className="p-2 rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-50">{accion === c.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="cotizacion-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            <h3 className="text-lg font-extrabold text-slate-900">Nueva cotización</h3>
            <div className="mt-4 space-y-3">
              <input value={form.titulo} onChange={(e) => setForm((f) => ({ ...f, titulo: e.target.value }))} data-testid="cot-titulo" placeholder="Título (ej. Suministro de materiales)" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
              <input value={form.destinatario_email} onChange={(e) => setForm((f) => ({ ...f, destinatario_email: e.target.value }))} data-testid="cot-destinatario" placeholder="Correo del destinatario" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
              <textarea value={form.descripcion} onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))} data-testid="cot-descripcion" rows={2} placeholder="Descripción (opcional)" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-1">Ítems</p>
                <div className="space-y-2">
                  {form.items.map((l, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input value={l.descripcion} onChange={(e) => setLinea(i, "descripcion", e.target.value)} data-testid={`cot-item-desc-${i}`} placeholder="Descripción" className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-sm" />
                      <input type="number" min="1" value={l.cantidad} onChange={(e) => setLinea(i, "cantidad", e.target.value)} data-testid={`cot-item-cant-${i}`} className="w-16 rounded-lg border border-slate-200 px-2 py-1.5 text-sm" />
                      <input type="number" min="0" step="0.01" value={l.precio_unitario} onChange={(e) => setLinea(i, "precio_unitario", e.target.value)} data-testid={`cot-item-precio-${i}`} placeholder="S/" className="w-20 rounded-lg border border-slate-200 px-2 py-1.5 text-sm" />
                      {form.items.length > 1 && <button onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }))} className="text-red-400 hover:text-red-600"><X size={16} /></button>}
                    </div>
                  ))}
                </div>
                <button onClick={() => setForm((f) => ({ ...f, items: [...f.items, { ...LINEA }] }))} data-testid="cot-agregar-item" className="mt-2 text-sm font-semibold" style={{ color: BLUE }}>+ Agregar ítem</button>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <span className="text-sm text-slate-500">Total estimado</span>
                <span className="text-lg font-extrabold text-slate-900" data-testid="cot-total">{soles(totalForm)}</span>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setModal(false)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button onClick={crear} disabled={guardando} data-testid="cot-enviar" className="px-5 py-2 rounded-lg text-white font-bold text-sm hover:brightness-110 disabled:opacity-60 inline-flex items-center gap-2" style={{ backgroundColor: BLUE }}>{guardando && <Loader2 size={15} className="animate-spin" />} Enviar cotización</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
